/**
 * Plugins da mesa, uma aba das Configurações da mesa: instalar (o diálogo de "Instalar plugin…", com
 * os oficiais, o Desk Cloud e o computador), atualizar um oficial que ganhou versão nova, remover e
 * ativar ou desativar as tabelas que cada um traz. Só mestres mexem; quem mais abrir a aba vê a lista.
 * As tabelas que vêm com o RPG Desk (o tracker de combate e as macros) não aparecem aqui: estão
 * sempre na mesa, no menu Painéis (docs/spec/room-menus.md).
 *
 * O original tem os plugins nas configurações gerais (`TfrmConfigurationPluginsPage`). No RPG Desk
 * eles são por mesa, então ficam nas configurações da mesa, a pedido do usuário.
 */
import { useState } from 'react';
import { useT } from '../../i18n/index.js';
import { isSheetType, pluginDockTypes, sheetsUsingPlugin } from '../../plugins/pluginTypes.js';
import { openPluginInstall } from '../../plugins/web/install.js';
import { officialUpdate, useOfficialPlugins } from '../../plugins/web/officialCatalog.js';
import { PluginIcon, roomPluginIcon } from '../../plugins/web/PluginParts.js';
import { AiGuideButton, WebPluginExtras } from '../../plugins/web/WebPluginSettings.js';
import type { OfficialPlugin, Room, RoomPlugin } from '../../shared.js';
import { useApp } from '../../store.js';
import { Button } from '../../ui/controls.js';
import { askConfirm } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { RoomTableRow } from '../RoomTables.js';
// a lista de plugins e de tabelas usa os estilos da tela de Configurações (e o card, os do settings.css)
import '../../config/config.css';

export function RoomPluginsPage({ room }: { room: Room }) {
  const t = useT();
  const plugins = useApp((s) => s.plugins);
  const isGm = useApp((s) => !!s.user && (room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes('gm')));
  const sendRaw = useApp((s) => s.sendRaw);
  // o catálogo diz quais plugins têm versão oficial mais nova (só quem instala precisa dele)
  const { plugins: catalog } = useOfficialPlugins(isGm);
  const [updating, setUpdating] = useState<string | null>(null);

  /** Atualiza para a versão oficial publicada: pergunta antes, porque as fichas abertas recarregam. */
  async function update(plugin: RoomPlugin, entry: OfficialPlugin) {
    const sure = await askConfirm({
      title: t('pluginInstall.updateTitle', { name: plugin.name, version: entry.version }),
      message: t('webInstall.updating', { from: plugin.version, to: entry.version }),
      action: t('webInstall.update'),
    });
    if (!sure) return;
    setUpdating(plugin.moduleId);
    const result = await useApp.getState().installOfficialPlugin(entry.id);
    setUpdating(null);
    if (result.ok) toast(t('library.pluginUpdated', { name: plugin.name, version: entry.version }), 'success');
    else toast(t('library.pluginFailed', { message: result.error ?? t('webInstall.failed') }), 'error');
  }

  async function remove(plugin: RoomPlugin) {
    const { library, setDockActive } = useApp.getState();
    const sheets = sheetsUsingPlugin(library, plugin, plugins);
    const dockTypes = new Set(pluginDockTypes(plugin).map((d) => d.id));
    const activeDocks = library.filter((i) => i.kind === 'dock' && !i.inactive && i.dataType !== null && dockTypes.has(i.dataType));
    const lines = [
      sheets === 0 ? t('config.plugins.remove.noSheets') : t(sheets === 1 ? 'config.plugins.remove.oneSheet' : 'config.plugins.remove.sheets', { count: sheets }),
      ...(sheets ? [t('config.plugins.remove.keepSheets')] : []),
      ...(activeDocks.length ? [t('config.plugins.remove.docks')] : []),
    ];
    const sure = await askConfirm({ title: t('config.plugins.remove.title', { name: plugin.name }), message: lines.join('\n\n'), action: t('config.plugins.remove.go'), danger: true });
    if (!sure) return;
    // tabelas do plugin saem da mesa, com os dados guardados (voltam se o plugin voltar)
    for (const dock of activeDocks) setDockActive(dock.id, false);
    sendRaw({ type: 'plugin.uninstall', moduleId: plugin.moduleId });
  }

  return (
    <section className="rd-prefs__card rd-prefs__plugins" aria-labelledby="rd-room-plugins-title">
      <header className="rd-prefs__cardHead rd-prefs__cardHead--row">
        <div>
          <h3 id="rd-room-plugins-title">{t('config.plugins.title')}</h3>
          <p className="rd-prefs__desc">{t(isGm ? 'config.plugins.hint' : 'config.plugins.hint.player')}</p>
        </div>
        {isGm ? (
          <div className="rd-plugins__actions">
            <Button variant="action" onClick={() => openPluginInstall()}>
              {t('config.plugins.install')}
            </Button>
            <AiGuideButton />
          </div>
        ) : null}
      </header>
      {plugins.length ? (
        <ul className="rd-plugins">
          {plugins.map((p) => {
            const docks = pluginDockTypes(p);
            const newer = isGm ? officialUpdate(p, catalog) : undefined;
            return (
              <li key={p.moduleId} className="rd-prefs__blockRow">
                <div className="rd-plugins__head">
                  <PluginIcon url={roomPluginIcon(p.web)} table={!p.dataTypes.some(isSheetType)} />
                  <div className="rd-plugins__info">
                    <span className="rd-plugins__name">
                      <strong>{p.name}</strong>
                      {p.web.officialId ? (
                        <span className="rd-pluginTag" title={t('config.plugins.officialHint')}>
                          {t('config.plugins.official')}
                        </span>
                      ) : null}
                    </span>
                    <span>
                      {t('config.plugins.version', { v: p.version || '—' })}
                      {p.web.author ? ` · ${t('webInstall.by', { author: p.web.author })}` : ''} · {p.dataTypes.map((d) => d.title).join(', ') || '—'}
                    </span>
                  </div>
                  {isGm ? (
                    <div className="rd-plugins__rowActions">
                      {newer ? (
                        <Button
                          variant="secondary"
                          busy={updating === p.moduleId}
                          aria-label={t('pluginInstall.updateLabel', { name: p.name, version: newer.version })}
                          onClick={() => void update(p, newer)}
                        >
                          {t('pluginInstall.updateTo', { version: newer.version })}
                        </Button>
                      ) : null}
                      <Button variant="secondary" aria-label={t('config.plugins.remove.title', { name: p.name })} onClick={() => void remove(p)}>
                        {t('config.plugins.remove')}
                      </Button>
                    </div>
                  ) : null}
                </div>
                <WebPluginExtras plugin={p} isGm={isGm} />
                {docks.length ? (
                  <ul className="rd-tables" aria-label={t('config.plugins.tables', { name: p.name })}>
                    {docks.map((d) => (
                      <RoomTableRow key={d.id} table={{ dataType: d.id, title: d.title }} canManage={isGm} />
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rd-prefs__blockRow rd-prefs__desc">{t('config.plugins.none', { room: room.name })}</p>
      )}
    </section>
  );
}
